/**
 * usePatientSearch - Hook for managing patient search and selection
 *
 * Handles:
 * - Patient selection state
 * - Recent patients list (stored in sessionStorage)
 * - Consent status fetching
 * - Mode switching support (via sessionStorage)
 */

import { useToast } from '@/hooks/use-toast';
import { normalizeKennitala, usePatientLookup } from '@/hooks/usePatientLookup';
import { extractDOB, formatSSN, searchPatientsInRegistry } from '@/lib/sessionUtils';
import type { Patient } from '@/types/session';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

const RECENT_PATIENTS_KEY = 'recentPatients';
const CURRENT_PATIENT_KEY = 'currentSelectedPatient';
const MAX_RECENT_PATIENTS = 5;

/** Minimal patient data persisted to sessionStorage (kt + name + transcriber_db id) */
interface StoredPatient {
  id: string;
  name: string;
  dbId?: number;
}

const toStoredPatient = (patient: Patient): StoredPatient => ({
  id: patient.id,
  name: patient.registryName || patient.name,
  dbId: patient.dbId,
});

const toPatient = (stored: StoredPatient): Patient => ({
  id: stored.id,
  name: stored.name,
  ssn: formatSSN(stored.id),
  dob: extractDOB(stored.id),
  dbId: stored.dbId,
});

/**
 * Load recent patients from sessionStorage
 */
const loadRecentPatients = (): Patient[] => {
  try {
    const saved = sessionStorage.getItem(RECENT_PATIENTS_KEY);
    if (!saved) return [];
    const stored: StoredPatient[] = JSON.parse(saved);
    return stored.map(toPatient);
  } catch {
    return [];
  }
};

/**
 * Load current patient from sessionStorage (for mode switching)
 */
const loadCurrentPatient = (): Patient | null => {
  try {
    const saved = sessionStorage.getItem(CURRENT_PATIENT_KEY);
    if (!saved) return null;
    return toPatient(JSON.parse(saved));
  } catch {
    return null;
  }
};

/**
 * Save current patient to sessionStorage (kt + name only)
 */
const saveCurrentPatient = (patient: Patient | null): void => {
  if (patient) {
    sessionStorage.setItem(CURRENT_PATIENT_KEY, JSON.stringify(toStoredPatient(patient)));
  } else {
    sessionStorage.removeItem(CURRENT_PATIENT_KEY);
  }
};

interface UsePatientSearchOptions {
  /** Session ID if resuming an existing session */
  resumeSessionId?: string;
  /** Whether to load saved patient on mount (for mode switching) */
  loadSavedPatient?: boolean;
  /** Isolated task-context selection never reads/writes the tab patient. */
  persistSelection?: boolean;
  lookupPatientInfo?: boolean;
}

export interface UsePatientSearchReturn {
  /** Currently selected patient */
  selectedPatient: Patient | null;
  /** Ref to selected patient for use in async callbacks */
  selectedPatientRef: React.RefObject<Patient | null>;
  /** Set the selected patient */
  setSelectedPatient: (patient: Patient | null) => void;
  /** Clear the selected patient */
  clearSelectedPatient: () => void;
  /** Attach the transcriber_db id to the selected patient, if it is still `patientId` */
  attachPatientDbId: (patientId: string, dbId: number) => void;
  /** On resume, drop the tab's restored patient unless it is the session's own patient (`audio.patient_id`) */
  reconcileWithSessionPatient: (sessionPatientDbId: number | null) => void;
  /** Recent patients list */
  recentPatients: Patient[];
  /** Add a patient to recent patients */
  addToRecentPatients: (patient: Patient) => void;
  /** Current search query */
  searchQuery: string;
  /** Set the search query */
  setSearchQuery: (query: string) => void;
  /** Search results from the registry */
  searchResults: Patient[];
  /** Whether patient info is being fetched */
  isFetchingPatientInfo: boolean;
  /** Whether a search is in progress */
  isSearching: boolean;
}

/**
 * Hook for managing patient search and selection state
 */
export const usePatientSearch = (options: UsePatientSearchOptions = {}): UsePatientSearchReturn => {
  const { resumeSessionId, loadSavedPatient = false, persistSelection = true, lookupPatientInfo = true } = options;
  const { toast } = useToast();

  // The stored patient is tab-wide, so on a resumed session (not a mode switch
  // of the same session) it may belong to another session until reconciled.
  const restoredPatientRef = useRef<Patient | null>(null);

  // Initialize patient state
  const [selectedPatient, setSelectedPatientState] = useState<Patient | null>(() => {
    if (!persistSelection) return null;
    const isModeSwitch = new URLSearchParams(window.location.search).get('modeSwitch') === 'true';
    if (resumeSessionId || (loadSavedPatient && isModeSwitch)) {
      const restored = loadCurrentPatient();
      if (resumeSessionId && !isModeSwitch) restoredPatientRef.current = restored;
      return restored;
    }
    // New session - clear any saved patient
    saveCurrentPatient(null);
    return null;
  });

  // Keep ref in sync for async callbacks
  const selectedPatientRef = useRef<Patient | null>(selectedPatient);

  // Recent patients from sessionStorage
  const [recentPatients, setRecentPatients] = useState<Patient[]>(() => (persistSelection ? loadRecentPatients() : []));

  const queryClient = useQueryClient();

  // Search state with debounce
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Sync ref with state
  useEffect(() => {
    selectedPatientRef.current = selectedPatient;
  }, [selectedPatient]);

  // Save current patient to sessionStorage when it changes
  useEffect(() => {
    if (persistSelection) saveCurrentPatient(selectedPatient);
  }, [selectedPatient, persistSelection]);

  /**
   * Set selected patient and persist to sessionStorage. Invalidates the
   * lookup cache so re-selecting the same patient always refetches (consent
   * may have changed since the last time this kennitala was looked up).
   */
  const setSelectedPatient = useCallback(
    (patient: Patient | null) => {
      restoredPatientRef.current = null;
      setSelectedPatientState(patient);
      if (patient && lookupPatientInfo)
        queryClient.invalidateQueries({ queryKey: ['patients', 'lookup', normalizeKennitala(patient.id)] });
    },
    [queryClient, lookupPatientInfo]
  );

  /**
   * Clear selected patient
   */
  const clearSelectedPatient = useCallback(() => {
    restoredPatientRef.current = null;
    setSelectedPatientState(null);
  }, []);

  /**
   * Attach the resolved transcriber_db id without refetching the lookup. Ignored
   * if the user switched patients while the id was being resolved.
   */
  const attachPatientDbId = useCallback((patientId: string, dbId: number) => {
    if (selectedPatientRef.current?.id === patientId) {
      selectedPatientRef.current = { ...selectedPatientRef.current, dbId };
    }
    setSelectedPatientState((current) => (current?.id === patientId ? { ...current, dbId } : current));
  }, []);

  const reconcileWithSessionPatient = useCallback((sessionPatientDbId: number | null) => {
    const restored = restoredPatientRef.current;
    restoredPatientRef.current = null;
    const current = selectedPatientRef.current;
    // Only the restored patient is in doubt; picking or clearing on this page resets restoredPatientRef.
    if (!restored || !current || current.id !== restored.id) return;
    if (current.dbId == null || current.dbId !== sessionPatientDbId) {
      selectedPatientRef.current = null;
      setSelectedPatientState(null);
    }
  }, []);

  /**
   * Add patient to recent patients list
   */
  const addToRecentPatients = useCallback(
    (patient: Patient) => {
      setRecentPatients((current) => {
        // Remove patient if already exists
        const filtered = current.filter((p) => p.id !== patient.id);
        // Add to beginning, keep only max
        const updated = [patient, ...filtered].slice(0, MAX_RECENT_PATIENTS);
        if (persistSelection) sessionStorage.setItem(RECENT_PATIENTS_KEY, JSON.stringify(updated.map(toStoredPatient)));
        return updated;
      });
    },
    [persistSelection]
  );

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  /**
   * Search patients in National Registry
   */
  const { data: searchResults = [], isFetching: isSearching } = useQuery({
    queryKey: ['patients', 'search', debouncedSearchQuery],
    queryFn: () => searchPatientsInRegistry(debouncedSearchQuery),
    enabled: debouncedSearchQuery.trim().length >= 4,
  });

  /**
   * Fetch patient info (consent status, registry name, FHIR ID)
   * Fires automatically when selectedPatient changes (including restore from sessionStorage)
   */
  const {
    data: patientInfo,
    error: patientInfoError,
    isFetching: isFetchingPatientInfo,
  } = usePatientLookup(selectedPatient?.id, { enabled: lookupPatientInfo });

  // Merge fetched patient info into selected patient state
  useEffect(() => {
    const patient = selectedPatientRef.current;
    if (lookupPatientInfo && patientInfo && patient) {
      const updatedPatient: Patient = {
        ...patient,
        registryName: patientInfo.name || patient.name,
        consentStatus: patientInfo.consentStatus,
        fhirPatientId: patientInfo.fhirPatientId ?? undefined,
      };
      setSelectedPatientState(updatedPatient);
      addToRecentPatients(updatedPatient);
    }
  }, [patientInfo, addToRecentPatients, lookupPatientInfo]);

  // Handle patient info fetch errors
  useEffect(() => {
    const patient = selectedPatientRef.current;
    if (lookupPatientInfo && patientInfoError && patient) {
      const errorPatient = {
        ...patient,
        consentStatus: 'error' as const,
      };
      setSelectedPatientState(errorPatient);
      addToRecentPatients(errorPatient);
      toast({
        title: 'Villa',
        description: 'Ekki tókst að sækja upplýsingar um sjúkling',
        variant: 'destructive',
      });
    }
  }, [patientInfoError, addToRecentPatients, toast, lookupPatientInfo]);

  return {
    selectedPatient,
    selectedPatientRef,
    setSelectedPatient,
    clearSelectedPatient,
    attachPatientDbId,
    reconcileWithSessionPatient,
    recentPatients,
    addToRecentPatients,
    searchQuery,
    setSearchQuery,
    searchResults,
    isFetchingPatientInfo,
    isSearching,
  };
};
